// Sprint 22.7 Phase 1 framework upgrade, session 4 (2026-05-12).
// Sentry init must be the first import so error capture sees React's
// lifecycle from the start. No-op when VITE_SENTRY_DSN_FRONTEND is
// unset at build time. See frontend/src/sentry.js for config.
import './sentry.js';
import * as Sentry from '@sentry/react';

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './index.css';

// Global fetch interceptor: adds Authorization header to all /api/ requests
// and reloads on 401 (session expired). This avoids modifying every component.
const originalFetch = window.fetch;
window.fetch = function (url, options = {}) {
  const isApiCall = typeof url === 'string' && url.startsWith('/api/');
  const isAuthRoute = typeof url === 'string' && url.startsWith('/api/auth/');

  if (isApiCall && !isAuthRoute) {
    const token = localStorage.getItem('korinth_token');
    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }

  return originalFetch(url, options).then(res => {
    if (res.status === 401 && isApiCall && !isAuthRoute) {
      localStorage.removeItem('korinth_token');
      window.location.reload();
    }
    return res;
  });
};

// Fallback UI for the top-level ErrorBoundary. If a React render
// error escapes every component-level boundary (today: none), this
// renders instead of a blank screen. The error has already been
// captured by Sentry (if DSN configured) by the time this renders.
function ErrorFallback({ error, resetError }) {
  return (
    <div style={{
      padding: '2rem',
      fontFamily: 'monospace',
      color: '#e5e5e5',
      background: '#0a0a0a',
      minHeight: '100vh',
    }}>
      <h1 style={{ color: '#ff6b6b' }}>KORINTH frontend error</h1>
      <p>The UI hit an unhandled error and stopped rendering.</p>
      <pre style={{ marginTop: '1rem', color: '#888', whiteSpace: 'pre-wrap' }}>
        {String(error?.message || error)}
      </pre>
      <button
        onClick={resetError}
        style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
      >
        Reload component
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
